import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { Button, TextArea, TextField } from '@orijinworks/frontend-commons';
import { useServiceFormHook } from '../hooks/useServiceFormHook';
import { CHARACTER_LIMITS } from '../constants';
import { useServiceFormModalStyles } from './service-form-modal.style';

const systemPropType = PropTypes.shape({
  systemId: PropTypes.number.isRequired,
  systemName: PropTypes.string,
});

const renderSystemField = (hook, classes) => {
  if (hook.addingSystem) {
    return (
      <Box className={`${classes.systemRow} ${classes.fullRow}`}>
        <TextField
          label="New system name"
          value={hook.newSystemName}
          onChange={(event) => hook.setNewSystemName(event.target.value)}
          variant="outlined"
          size="small"
          fullWidth
        />
        <Button
          size="small"
          color="primary"
          disabled={hook.systemSaving || hook.newSystemName.trim() === ''}
          onClick={hook.handleCreateSystem}
        >
          Add
        </Button>
        <Button size="small" disabled={hook.systemSaving} onClick={() => hook.setAddingSystem(false)}>
          Cancel
        </Button>
      </Box>
    );
  }

  return (
    <Box className={`${classes.systemRow} ${classes.fullRow}`}>
      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel id="svc-system-label">System</InputLabel>
        <Select
          labelId="svc-system-label"
          value={hook.form.systemKey}
          onChange={(event) => hook.patch({ systemKey: String(event.target.value) })}
          label="System"
        >
          {hook.systems.map((system) => (
            <MenuItem key={system.systemId} value={String(system.systemId)}>
              {system.systemName || `System #${system.systemId}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button size="small" color="primary" onClick={() => hook.setAddingSystem(true)}>
        Add new system
      </Button>
    </Box>
  );
};

const renderMetadataEditor = (hook, classes) => (
  <Box className={classes.fullRow}>
    <Typography variant="body2" className={classes.sectionLabel}>
      Additional metadata
    </Typography>
    {hook.form.metadataPairs.map((pair, index) => (
      <Box key={index} className={classes.metadataRow}>
        <TextField
          className={classes.metadataKey}
          label="Key"
          value={pair.key}
          onChange={(event) => hook.patchMetadataPair(index, { key: event.target.value })}
          variant="outlined"
          size="small"
        />
        <TextField
          className={classes.metadataValue}
          label="Value"
          value={pair.value}
          onChange={(event) => hook.patchMetadataPair(index, { value: event.target.value })}
          variant="outlined"
          size="small"
        />
        <IconButton size="small" aria-label="remove metadata entry" onClick={() => hook.removeMetadataPair(index)}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button size="small" color="primary" startIcon={<AddIcon />} onClick={hook.addMetadataPair}>
      Add metadata field
    </Button>
  </Box>
);

export function ServiceFormModal({ open, service, systems, onClose, onSaved, onSystemCreated }) {
  const classes = useServiceFormModalStyles();
  const hook = useServiceFormHook({ onSaved, onSystemCreated, open, service, systems });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {hook.isEdit ? 'Modify service' : 'Create service'}
        <Typography variant="body2" className={classes.subtitle}>
          {hook.isEdit
            ? 'Service name is immutable. CMS records are not re-synced on modify.'
            : 'A matching Strapi CMS record is created automatically.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.formGrid}>
          <Box>
            <TextField
              label="Service name"
              value={hook.form.serviceName}
              onChange={(event) => hook.patch({ serviceName: event.target.value })}
              variant="outlined"
              size="small"
              fullWidth
              disabled={hook.isEdit}
              required
              slotProps={{ htmlInput: { maxLength: CHARACTER_LIMITS.SERVICE_NAME } }}
            />
            {hook.isEdit && (
              <Typography className={classes.immutableHint}>Cannot be changed after creation.</Typography>
            )}
          </Box>
          <TextField
            label="Display name"
            value={hook.form.displayName}
            onChange={(event) => hook.patch({ displayName: event.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: CHARACTER_LIMITS.DISPLAY_NAME } }}
          />

          {renderSystemField(hook, classes)}

          <TextField
            label="CMS key (SKU)"
            value={hook.form.cmsKey}
            onChange={(event) => hook.patch({ cmsKey: event.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: CHARACTER_LIMITS.CMS_KEY } }}
          />
          <TextField
            label="Icon key"
            value={hook.form.iconKey}
            onChange={(event) => hook.patch({ iconKey: event.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            slotProps={{ htmlInput: { maxLength: CHARACTER_LIMITS.ICON_KEY } }}
          />

          <Box className={classes.fullRow}>
            <TextArea
              label="Description"
              value={hook.form.description}
              onChange={(event) => hook.patch({ description: event.target.value })}
              variant="outlined"
              rows={5}
              slotProps={{ htmlInput: { maxLength: CHARACTER_LIMITS.DESCRIPTION } }}
            />
            <Typography className={classes.counter}>
              {hook.form.description.length} / {CHARACTER_LIMITS.DESCRIPTION}
            </Typography>
          </Box>

          <TextField
            className={classes.fullRow}
            label="Main URL"
            value={hook.form.mainUrl}
            onChange={(event) => hook.patch({ mainUrl: event.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            slotProps={{ htmlInput: { maxLength: CHARACTER_LIMITS.MAIN_URL } }}
          />

          <TextField
            label="Contracted licenses"
            value={hook.form.contractedLicenses}
            onChange={(event) => hook.patch({ contractedLicenses: event.target.value })}
            variant="outlined"
            size="small"
            type="number"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={hook.form.explorable}
                onChange={(event) => hook.patch({ explorable: event.target.checked })}
                color="primary"
              />
            }
            label="Explorable"
          />

          <TextField
            label="Start date"
            value={hook.form.startDate}
            onChange={(event) => hook.patch({ startDate: event.target.value })}
            variant="outlined"
            size="small"
            type="date"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="End date"
            value={hook.form.endDate}
            onChange={(event) => hook.patch({ endDate: event.target.value })}
            variant="outlined"
            size="small"
            type="date"
            fullWidth
            disabled={hook.form.active}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <FormControlLabel
            className={classes.fullRow}
            control={
              <Switch
                checked={hook.form.active}
                onChange={(event) => hook.patch({ active: event.target.checked })}
                color="primary"
              />
            }
            label={hook.form.active ? 'Active (no end date)' : 'Inactive'}
          />

          {renderMetadataEditor(hook, classes)}
        </Box>

        {hook.formError && (
          <Typography variant="body2" className={classes.error}>
            {hook.formError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary" disabled={hook.saving}>
          Cancel
        </Button>
        <Button onClick={hook.submit} color="primary" disabled={hook.saving}>
          {hook.isEdit ? 'Save changes' : 'Create service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ServiceFormModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onSystemCreated: PropTypes.func,
  open: PropTypes.bool.isRequired,
  service: PropTypes.object,
  systems: PropTypes.arrayOf(systemPropType),
};

ServiceFormModal.defaultProps = {
  onSystemCreated: undefined,
  service: null,
  systems: [],
};
